<template>
  <div>
    <!-- 统计卡片区域 -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <el-card 
        v-for="stat in stats" 
        :key="stat.title" 
        class="hover:shadow-lg transition-shadow duration-300 overflow-hidden border border-gray-100 dark:border-gray-700"
        :body-style="{ padding: '16px' }"
      >
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-500 text-sm">{{ stat.title }}</p>
            <p class="text-2xl font-semibold mt-2">{{ stat.value }}</p>
            <div class="flex items-center mt-2 space-x-1">
              <el-icon v-if="stat.trend > 0" class="text-green-500"><ArrowUp /></el-icon>
              <el-icon v-if="stat.trend < 0" class="text-red-500"><ArrowDown /></el-icon>
              <span :class="stat.trend > 0 ? 'text-green-500' : 'text-red-500'">{{ Math.abs(stat.trend) }}%</span>
              <span class="text-gray-400 text-xs ml-1">{{ $t('admin.home.dashboard.stats.comparedToLastWeek') }}</span>
            </div>
          </div>
          <div :class="`bg-${stat.bgColor} p-3 rounded-lg`">
            <el-icon :size="24" :color="stat.iconColor">
              <component :is="getIconComponent(stat.icon)" />
            </el-icon>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 图表区域 -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <el-card class="w-full h-full" shadow="hover">
        <template #header>
          <div class="flex items-center justify-between">
            <span>{{ $t('admin.home.dashboard.charts.visitTrend') }}</span>
            <el-select v-model="trendTimeRange" placeholder="Select" size="small" class="w-28">
              <el-option :label="$t('admin.home.dashboard.charts.timeRanges.week')" value="week" />
              <el-option :label="$t('admin.home.dashboard.charts.timeRanges.month')" value="month" />
              <el-option :label="$t('admin.home.dashboard.charts.timeRanges.year')" value="year" />
            </el-select>
          </div>
        </template>
        <div class="h-80 flex items-center justify-center" v-loading="isChartsLoading">
          <div ref="trendChartRef" class="w-full h-full"></div>
        </div>
      </el-card>
      <el-card class="w-full h-full" shadow="hover">
        <template #header>
          <div class="flex items-center justify-between">
            <span>{{ $t('admin.home.dashboard.charts.userDistribution') }}</span>
            <el-radio-group v-model="userDistributionType" size="small">
              <el-radio-button value="type" :label="$t('admin.home.dashboard.charts.distributionTypes.type')">{{ $t('admin.home.dashboard.charts.distributionTypes.type') }}</el-radio-button>
              <el-radio-button value="region" :label="$t('admin.home.dashboard.charts.distributionTypes.region')">{{ $t('admin.home.dashboard.charts.distributionTypes.region') }}</el-radio-button>
            </el-radio-group>
          </div>
        </template>
        <div class="h-80 flex items-center justify-center" v-loading="isChartsLoading">
          <div ref="pieChartRef" class="w-full h-full"></div>
        </div>
      </el-card>
    </div>

    <!-- 活动列表 -->
    <el-card class="w-full" shadow="hover">
      <template #header>
        <div class="flex items-center justify-between">
          <span>{{ $t('admin.home.dashboard.activities.title') }}</span>
          <el-button type="primary" plain size="small" text>{{ $t('admin.home.dashboard.activities.viewAll') }}</el-button>
        </div>
      </template>
      <div class="overflow-auto" style="max-height: 400px;" v-loading="isActivitiesLoading">
        <div v-for="activity in activities" :key="activity.id" class="py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors duration-200 px-2 rounded">
          <div class="flex items-center justify-between w-full">
            <div class="flex items-center">
              <el-avatar :size="32" :style="{ backgroundColor: activity.avatarColor }">
                {{ activity.avatar }}
              </el-avatar>
              <div class="ml-4">
                <p class="font-semibold">{{ activity.title }}</p>
                <p class="text-gray-500 text-sm">{{ activity.description }}</p>
              </div>
            </div>
            <span class="text-gray-400 text-sm whitespace-nowrap">{{ activity.time }}</span>
          </div>
        </div>
        
        <div v-if="activities.length === 0" class="py-10 text-center text-gray-500">
          {{ $t('admin.home.dashboard.activities.noActivities') }}
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ArrowUp, ArrowDown, User, Histogram, Money, PieChart, View } from '@element-plus/icons-vue'
import { onMounted, ref, nextTick, watch, onBeforeUnmount, computed } from 'vue'
import * as echarts from 'echarts'

// 图标映射函数
const getIconComponent = (iconName) => {
  const iconMap = {
    'icon-user': User,
    'icon-fire': Histogram,
    'icon-money': Money,
    'icon-chart-pie': PieChart,
    'icon-view': View
  }
  return iconMap[iconName] || User
}

// 加载状态
const isChartsLoading = ref(true)
const isActivitiesLoading = ref(true)

// 图表选择器
const trendTimeRange = ref('week')
const userDistributionType = ref('type')

// 图表引用
const trendChartRef = ref(null)
const pieChartRef = ref(null)
let trendChart = null
let pieChart = null

// 统计数据
const { t } = useI18n()
const stats = computed(() => [
  {
    title: t('admin.home.dashboard.stats.totalUsers'),
    value: '12,846',
    trend: 12.5,
    icon: 'icon-user',
    bgColor: 'blue-50',
    iconColor: '#4B96FF'
  },
  {
    title: t('admin.home.dashboard.stats.activeToday'),
    value: '1,286',
    trend: -2.5,
    icon: 'icon-fire',
    bgColor: 'amber-50',
    iconColor: '#FF9E0F'
  },
  {
    title: t('admin.home.dashboard.stats.totalRevenue'),
    value: '¥126,560',
    trend: 8.2,
    icon: 'icon-money',
    bgColor: 'green-50',
    iconColor: '#52C41A'
  },
  {
    title: t('admin.home.dashboard.stats.conversionRate'),
    value: '25.6%',
    trend: 4.1,
    icon: 'icon-chart-pie',
    bgColor: 'purple-50',
    iconColor: '#722ED1'
  },
])

// 访问趋势数据
const generateTrendData = (range) => {
  // 模拟不同时间范围的数据
  const data = {
    week: [
      { x: t('admin.home.dashboard.charts.weekdays.monday'), y: 100 },
      { x: t('admin.home.dashboard.charts.weekdays.tuesday'), y: 180 },
      { x: t('admin.home.dashboard.charts.weekdays.wednesday'), y: 150 },
      { x: t('admin.home.dashboard.charts.weekdays.thursday'), y: 280 },
      { x: t('admin.home.dashboard.charts.weekdays.friday'), y: 200 },
      { x: t('admin.home.dashboard.charts.weekdays.saturday'), y: 350 },
      { x: t('admin.home.dashboard.charts.weekdays.sunday'), y: 300 },
    ],
    month: Array.from({ length: 30 }, (_, i) => ({
      x: `${i + 1}`,
      y: Math.floor(Math.random() * 500) + 100
    })),
    year: Array.from({ length: 12 }, (_, i) => ({
      x: `${i + 1}`,
      y: Math.floor(Math.random() * 1000) + 500
    }))
  }
  return data[range] || data.week
}

// 用户分布数据
const generateUserDistributionData = (type) => {
  const data = {
    type: [
      { name: t('admin.home.dashboard.charts.userTypes.newUsers'), value: 27 },
      { name: t('admin.home.dashboard.charts.userTypes.activeUsers'), value: 45 },
      { name: t('admin.home.dashboard.charts.userTypes.inactiveUsers'), value: 28 },
    ],
    region: [
      { name: t('admin.home.dashboard.charts.regions.east'), value: 35 },
      { name: t('admin.home.dashboard.charts.regions.north'), value: 20 },
      { name: t('admin.home.dashboard.charts.regions.south'), value: 18 },
      { name: t('admin.home.dashboard.charts.regions.west'), value: 15 },
      { name: t('admin.home.dashboard.charts.regions.northeast'), value: 12 },
    ]
  }
  return data[type] || data.type
}

// 监听时间范围变化
watch(trendTimeRange, (newValue) => {
  updateTrendChart(newValue)
})

// 监听用户分布类型变化
watch(userDistributionType, (newValue) => {
  updatePieChart(newValue)
})

const visitData = ref(generateTrendData('week'))
const userData = ref(generateUserDistributionData('type'))

// 活动列表数据 (使用示例数据，实际项目中应从API获取)
const activities = ref([
  {
    id: 1,
    avatar: 'Z',
    avatarColor: '#165DFF',
    title: 'Zhang San completed task approval',
    description: 'Approved "New Feature Development" task',
    time: '10 minutes ago'
  },
  {
    id: 2,
    avatar: 'L',
    avatarColor: '#14C9C9',
    title: 'Li Si updated project documentation',
    description: 'Updated product requirements document v2.0',
    time: '30 minutes ago'
  },
  {
    id: 3,
    avatar: 'W',
    avatarColor: '#FF7D00',
    title: 'Wang Wu deployed new version',
    description: 'v1.2.3 has been successfully deployed to production',
    time: '1 hour ago'
  },
])

// 初始化趋势图表
const initTrendChart = () => {
  if (trendChartRef.value) {
    trendChart = echarts.init(trendChartRef.value)
    updateTrendChart(trendTimeRange.value)
  }
}

// 更新趋势图表
const updateTrendChart = (range) => {
  if (!trendChart) return
  
  const data = generateTrendData(range)
  visitData.value = data
  
  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: data.map(item => item.x),
      axisLine: {
        lineStyle: {
          color: '#eee'
        }
      },
      axisLabel: {
        color: '#666'
      }
    },
    yAxis: {
      type: 'value',
      axisLine: {
        show: false
      },
      splitLine: {
        lineStyle: {
          color: '#eee'
        }
      }
    },
    series: [{
        name: t('admin.home.dashboard.charts.chartLabels.visits'),
      type: 'line',
      smooth: true,
      data: data.map(item => item.y),
      itemStyle: {
        color: '#165DFF'
      },
      lineStyle: {
        width: 3,
        shadowColor: 'rgba(22, 93, 255, 0.3)',
        shadowBlur: 10
      },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {
            offset: 0,
            color: 'rgba(22, 93, 255, 0.3)'
          },
          {
            offset: 1,
            color: 'rgba(22, 93, 255, 0.05)'
          }
        ])
      }
    }]
  }
  trendChart.setOption(option)
}

// 初始化饼图
const initPieChart = () => {
  if (pieChartRef.value) {
    pieChart = echarts.init(pieChartRef.value)
    updatePieChart(userDistributionType.value)
  }
}

// 更新饼图
const updatePieChart = (type) => {
  if (!pieChart) return
  
  const data = generateUserDistributionData(type)
  userData.value = data
  
  const option = {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'horizontal',
      bottom: 'bottom',
      data: data.map(item => item.name)
    },
    series: [
      {
        name: t('admin.home.dashboard.charts.chartLabels.userDistribution'),
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 16,
            fontWeight: 'bold'
          }
        },
        labelLine: {
          show: false
        },
        data: data.map(item => ({
          value: item.value,
          name: item.name
        }))
      }
    ]
  }
  pieChart.setOption(option)
}

// 监听窗口大小变化
const handleResize = () => {
  trendChart?.resize()
  pieChart?.resize()
}

// 获取模拟数据
const fetchData = async () => {
  isChartsLoading.value = true
  isActivitiesLoading.value = true
  
  // 模拟数据加载延迟
  await new Promise(resolve => setTimeout(resolve, 800))
  
  isChartsLoading.value = false
  isActivitiesLoading.value = false
}

onMounted(async () => {
  await fetchData()
  
  nextTick(() => {
    initTrendChart()
    initPieChart()
    window.addEventListener('resize', handleResize)
  })
})

// 组件卸载前清理
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  trendChart?.dispose()
  pieChart?.dispose()
})

defineOptions({ layout: Admin })
</script>
